"use client";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useRef } from "react";
import Link from "next/link";
import { useDispatch } from "react-redux";
import { openModal } from "@/redux/features/GoldInvestModal/goldInvestModalSlice";

export default function GoldPriceTrendSection() {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const dispatch = useDispatch();

  const options: Highcharts.Options = {
    chart: {
      type: "area",
      backgroundColor: "transparent",
      height: 500,
    },
    title: { text: "" },
    xAxis: { visible: false },
    yAxis: { visible: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      series: {
        animation: true,
        marker: { enabled: false },
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter: function () {
            // @ts-expect-error Highcharts type mismatch for 'this.point'
            const yearValue = this.point.name;
            return `
              <div class="rounded-full bg-[#4D4000] text-white text-2xs sm:text-base font-normal border sm:border-2 border-[#C89900] py-px sm:py-1 px-2 sm:px-4">
                ${yearValue}
              </div>
            `;
          },
          align: "center",
          y: 15,
        },
        lineWidth: 3,
        color: "#f9d65d",
      },
    },
    series: [
      {
        type: "area",
        name: "Progress",
        data: [
          { y: 1, name: "1848" },
          { y: 1.2, name: "1929" },
          { y: 1, name: "1970" },
          { y: 2, name: "2000" },
          { y: 0.9, name: "2007" },
          { y: 4, name: "2010" },
          { y: 5, name: "2020" },
          { y: 7, name: "2022" },
        ],
        color: {
          linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
          stops: [
            [0, "#CBAD00"],
            [0.5, "#FFFFFF"],
            [1, "#CBAD00"],
          ],
        },
        lineWidth: 5,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(134, 105, 0, 0.54)"],
            [1, "rgba(174, 151, 0, 0.0)"],
          ],
        },
        marker: {
          enabled: false,
        },
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            chart: {
              height: 400,
            },
            plotOptions: {
              series: {
                dataLabels: {
                  style: {
                    fontSize: "12px",
                  },
                },
              },
            },
          },
        },
        {
          condition: {
            minWidth: 769,
          },
          chartOptions: {
            chart: {
              height: 500,
            },
            plotOptions: {
              series: {
                dataLabels: {
                  style: {
                    fontSize: "14px",
                  },
                },
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="w-full mx-auto 2xl:max-w-7xl h-400 sm:h-500 relative">
      <div className="absolute z-1 top-4 left-6 md:top-16 max-w-66.25 xxxs:max-w-76.25 sm:max-w-118.75">
        <h2 className="text-xl sm:text-2xl md:text-3xl text-white font-bold mb-3 sm:mb-5">Gold Price Trends</h2>
        <p className="text-white leading-1.5 font-light mb-4 sm:mb-7 text-sm sm:text-base sm:leading-6 md:text-xl md:leading-7">Gold’s rising price trend makes it a smart choice for consistent returns and security, both now and in uncertain futures.</p>

        <div className="flex flex-col sm:flex-row items-center w-full gap-3 text-center max-w-48 sm:max-w-full">
          <button onClick={() => dispatch(openModal())} className="shrink-0 w-full sm:w-1/2 text-2xs sm:text-2sm bg-[#C89900] text-white font-medium py-2.5 xxxs:py-3 rounded-xl shine-button-hover">
            Invest in Gold
          </button>
          <Link href="/buy-digital-gold/gold-price" className="text-2xs sm:text-2sm transition w-full bg-transparent duration-300 border border-[#C89900] sm:hover:bg-[#C89900] text-white py-2.5 xxxs:py-3 rounded-xl">
            Check Price in Your City
          </Link>
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} className="relative z-0" />
    </div>
  );
}
